import UniData from '@/common/classes/UniData'
import { TypeSerializer } from '@/common/utils/typeSerializer'

export type TableColumnMeta = {
  isPrimaryKey?: { _text?: string }
  name?: { _text?: string }
}

export type TableSelectionRow = {
  _rowIndex?: unknown
  fields?: Record<string, unknown>
  [key: string]: unknown
}

function toUniDataField(value: unknown): unknown {
  return TypeSerializer.serialize(value)
}

function normalizeColumnsArray(rawColumns: unknown): TableColumnMeta[] {
  if (!rawColumns) return []
  return Array.isArray(rawColumns) ? (rawColumns as TableColumnMeta[]) : [rawColumns as TableColumnMeta]
}

/** Имена primary-key колонок из meta columns.column. */
export function resolvePrimaryKeyColumnNames(rawColumns: unknown): string[] {
  return normalizeColumnsArray(rawColumns)
    .filter((c) => c?.isPrimaryKey?._text === 'true' || c?.isPrimaryKey?._text === '1')
    .map((c) => c.name?._text as string)
    .filter(Boolean)
}

function pickRowFieldValue(obj: TableSelectionRow | null | undefined, key: string): string | null {
  if (!obj) return null
  const fields = obj.fields as Record<string, unknown> | undefined
  const fromFields = fields?.[key]
  if (fromFields != null && typeof fromFields === 'object' && 'value' in (fromFields as object)) {
    const v = (fromFields as { value?: unknown }).value
    return v != null ? String(v) : null
  }
  if (fromFields != null) {
    return String(fromFields)
  }
  const direct = obj[key]
  if (direct != null && typeof direct === 'object' && 'value' in (direct as object)) {
    const v = (direct as { value?: unknown }).value
    return v != null ? String(v) : null
  }
  if (direct != null) {
    return String(direct)
  }
  return null
}

/** Поля UniData для getSelectedPrimaryKey (PK + rowNum). */
export function buildSelectedPrimaryKeyFields(input: {
  rawColumns: unknown
  selectionRow: TableSelectionRow | null | undefined
}): Record<string, unknown> {
  const pkColNames = resolvePrimaryKeyColumnNames(input.rawColumns)
  if (pkColNames.length === 0) {
    return {}
  }

  const result: Record<string, unknown> = {}
  const row = input.selectionRow ?? null

  for (const key of pkColNames) {
    const v = pickRowFieldValue(row, key)
    if (v != null) {
      result[key] = toUniDataField(v)
    }
  }

  const rowNumRaw = row?._rowIndex ?? '0'
  result.rowNum = toUniDataField(rowNumRaw)

  return result
}

export function buildSelectedPrimaryKeyUniData(input: {
  rawColumns: unknown
  selectionRow: TableSelectionRow | null | undefined
}): UniData {
  const fields = buildSelectedPrimaryKeyFields(input)
  return new UniData(fields)
}

/** WS-ответ runCode (fields.fields). */
export function buildSelectedPrimaryKeyWsResponse(input: {
  rawColumns: unknown
  selectionRow: TableSelectionRow | null | undefined
}): { fields: { fields: Record<string, unknown> } } {
  return {
    fields: {
      fields: buildSelectedPrimaryKeyFields(input),
    },
  }
}

export function resolveElementType(elementType: unknown): string {
  return elementType != null ? String(elementType) : ''
}
